// Posting tab (LP-309 Phase 1, slice A): is automatic posting on, what it could not do (the
// exceptions), and the posting map (read only). Invoices, payments and Concria Oy lines,
// payments and bills post themselves in the database; this tab shows what needs a person.
// BooksPage gives every tab { me, accounts, deals, reloadShared, onGo }.

import { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { api } from '../../lib/api';
import { Money, Empty, fmtDate, centsToInput, parseCents, inputCls, btn2Cls, cardCls, thCls, tdCls } from './shared';

export const enabled = true;

// What each kind of exception means and what to do about it.
const KIND = {
  not_posted: { title: 'Not in Books', help: 'Dated after the cutover, with no entry. With posting off they wait for go-live (decide each one then); with posting on they happened while it was off.' },
  no_usd: { title: 'Agreed, but no USD amount', help: 'Enter the USD amount (or an FX rate) on the Concria Oy line; it posts when agreed again.' },
  left_behind: { title: 'Entries left behind', help: 'Changed while posting was off: the entry is still in Books. Reverse or delete it by hand in Transactions.' },
  reversed_agreed: { title: 'Agreed again after a write-off', help: 'Agreed again while posting was off; its entry was reversed at the write-off, so it nets to zero in Books. Delete the reversal and the original entry in Transactions, then mark the line claimed and agreed again (posting on) to post it afresh.' },
  tax_not_split: { title: 'Sales tax not split', help: 'The order charges tax but the invoice carries none (milestone invoices, until invoices carry their tax): the tax share sits in Product Sales.' },
  fx_rate: { title: 'EUR settled at another rate', help: 'Post the FX difference to 4950 (FX gain or loss).' },
  manual_dup: { title: 'Possible duplicate', help: 'A manual entry with the same cash amount within 7 days of an automatic one. If it is the same money, delete one.' },
};
const REASON = { posting_off: 'Waiting: posting off', not_posted: 'Not posted' };

export default function PostingTab({ me }) {
  const [data, setData] = useState(null);
  const [map, setMap] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    try {
      const [x, m] = await Promise.all([api.get('/books/posting/exceptions'), api.get('/books/posting/map')]);
      setData(x); setMap(m.rows); setError('');
    } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (error && !data) return <p className="text-sm text-rose-600">{error}</p>;
  if (!data) return <p className="text-sm text-[var(--text-secondary)]">Loading...</p>;
  const st = data.state;
  const groups = Object.keys(KIND).map((k) => [k, data.exceptions.filter((e) => e.kind === k)]).filter(([, rows]) => rows.length);

  return (
    <div className="space-y-4">
      <div className={`${cardCls} flex flex-wrap items-start justify-between gap-3`}>
        <div className="text-sm">
          <div className="font-semibold text-[var(--text-primary)]">
            {st.on ? 'Automatic posting is on' : 'Automatic posting is off'}
          </div>
          <div className="text-[var(--text-secondary)]">
            {!st.installed ? 'The posting layer is not installed yet.'
              : st.on ? `Since ${fmtDate(st.posting_enabled_from)}. Invoices post when sent and customer payments when recorded; Concria Oy lines when agreed, their payments when recorded, bills when paid.`
                : st.posting_enabled_from ? `Switches on ${fmtDate(st.posting_enabled_from)}.` : 'Nothing posts until the go-live date is set.'}
            {st.cutover_date ? ` Anything dated on or before ${fmtDate(st.cutover_date)} (the cutover) is history from QuickBooks and never posts.` : ' The cutover date is not set.'}
          </div>
        </div>
        <button className={btn2Cls} onClick={load}><RefreshCw size={14} /> Refresh</button>
      </div>
      {error && <p className="text-sm text-rose-600">{error}</p>}

      {groups.length === 0 ? <Empty>Nothing needs attention.</Empty> : groups.map(([kind, rows]) => (
        <div key={kind} className={cardCls}>
          <h2 className="font-semibold text-[var(--text-primary)]">{KIND[kind].title} ({rows.length})</h2>
          <p className="mb-2 text-xs text-[var(--text-secondary)]">{KIND[kind].help}</p>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead><tr><th className={thCls}>Date</th><th className={thCls}>Item</th><th className={`${thCls} text-right`}>Amount</th><th className={thCls}>Status</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.kind}:${r.ref}:${r.entry_id || ''}`} className="border-t border-[var(--border)]">
                    <td className={`${tdCls} whitespace-nowrap`}>{r.date ? fmtDate(String(r.date).slice(0, 10)) : ''}</td>
                    <td className={tdCls}>
                      <div className="text-[var(--text-primary)]">{r.label}</div>
                      <div className="text-xs text-[var(--text-secondary)]">{r.ref}{r.detail ? ` · ${r.detail}` : ''}</div>
                      {kind === 'fx_rate' && me.can_edit && st.on && <FxForm row={r} onDone={load} />}
                    </td>
                    <td className={`${tdCls} text-right`}>{r.amount_cents != null ? <Money cents={r.amount_cents} /> : ''}</td>
                    <td className={`${tdCls} text-xs`}>{REASON[r.reason] || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {data.skipped.count > 0 && (
        <details className={cardCls}>
          <summary className="cursor-pointer text-sm text-[var(--text-secondary)]">{data.skipped.count} on or before the cutover (in the QuickBooks history; never posted)</summary>
          <ul className="mt-2 space-y-1 text-xs">
            {data.skipped.rows.map((r) => <li key={r.ref}>{fmtDate(r.date)} · {r.label} · <Money cents={r.amount_cents} /></li>)}
          </ul>
        </details>
      )}

      <div className={cardCls}>
        <h2 className="font-semibold text-[var(--text-primary)]">Posting map</h2>
        <p className="mb-2 text-xs text-[var(--text-secondary)]">Which account each automatic posting uses. Read only here.</p>
        {!map ? null : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead><tr><th className={thCls}>Event</th><th className={thCls}>Role</th><th className={thCls}>Account</th><th className={thCls}>Note</th></tr></thead>
              <tbody>
                {map.map((m) => (
                  <tr key={m.id} className="border-t border-[var(--border)]">
                    <td className={tdCls}>{m.event}</td>
                    <td className={tdCls}>{m.role}</td>
                    <td className={tdCls}>{m.account_code} {m.account_name || ''}{!m.account_active && <span className="ml-1 text-xs text-amber-700 dark:text-amber-400">(inactive)</span>}</td>
                    <td className={`${tdCls} text-xs text-[var(--text-secondary)]`}>{m.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// Chad posts the FX difference of a settled EUR line (fx:<line id>, to 4950). The amount and
// direction start from the suggestion and can be changed.
function FxForm({ row, onDone }) {
  const [direction, setDirection] = useState(row.suggested_direction || 'loss');
  const [amount, setAmount] = useState(centsToInput(row.amount_cents || null));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const cents = parseCents(amount);
  const post = async () => {
    setBusy(true); setError('');
    try { await api.post(`/books/posting/fx/${row.row_id}`, { direction, amount_cents: cents }); onDone(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <select className={inputCls} value={direction} onChange={(e) => setDirection(e.target.value)}>
        <option value="loss">Loss</option><option value="gain">Gain</option>
      </select>
      <input className={`${inputCls} w-28 text-right`} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
      <button className={btn2Cls} disabled={busy || !(cents > 0)} onClick={post}>Post FX difference</button>
      {error && <span className="text-xs text-rose-600">{error}</span>}
    </div>
  );
}
